import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import { trackDeleteByRangeEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import restDaysCollection from 'modules/shift_management/store/api/collections/restDays'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'

interface Props {
  startDate: string
  endDate: string
  employeeIds: number[]
  destroyBackUpShifts: boolean
  contractVersions: ContractVersions
  destroyAutomaticOnly: boolean
}

const useDestroyByRange = ({
  startDate,
  endDate,
  employeeIds,
  destroyBackUpShifts,
  contractVersions,
  destroyAutomaticOnly,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }] = useSchedule()
  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)
  const restDayFlag = hasFeatureFlag(ShiftManagementFeatureFlags.SHIFT_MANAGEMENT_REST_DAY)

  const startDateParam = dateUIToBackend(startDate)
  const endDateParam = dateUIToBackend(endDate)

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const deletedShifts = await shiftsCollection.rpc('bulk_destroy', {
        start_at: startDateParam,
        end_at: endDateParam,
        employee_ids: employeeIds,
        destroy_backup_shifts: destroyBackUpShifts,
        destroy_automatic_shifts: destroyAutomaticOnly,
      })

      const deletedRestDays = restDayFlag
        ? await restDaysCollection.rpc('bulk_destroy', {
            start_at: startDateParam,
            end_at: endDateParam,
            employee_ids: employeeIds,
          })
        : []

      trackDeleteByRangeEvent(deletedShifts, deletedRestDays, view, startDateParam, endDateParam)

      await refreshShiftsRows(shiftsRows, [])

      closePortal()
    },
    {
      errorFlash: true,
      successMessage: i18n.t('shift_management.actions.delete_shifts_success'),
    }
  )

  return {
    onBulkDestroyShifts: performAction,
    submitting,
  }
}

export default useDestroyByRange
